// TimelineRow — one moment, translated from log jargon to author language.
//
// The load-bearing translation step (searchable-history.md §0): the log holds
// `material_element_child_moved`; this renders "edited Fractions quiz (12
// changes)". A row REVEALS its subject (opens the material / conversation) —
// never rewinds the world — so the subject is a button that fires `onReveal`.
// Two kinds carry an extra door: an `edited_material` row expands into the
// per-resource file-history lens (`onExpandResource`); a fact row doors into
// the Knowledge-bank lens (`onOpenKnowledge`). Everything the row shows arrives
// resolved (subject title, actor, relative `when`) — pure/props, no clock.
//
// The sentence per kind is a `match` over the discriminant, each arm its own
// localised phrase, so a new kind breaks the switch until its sentence is
// written (the point).

export { TimelineRow };

import { Trans } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type { IconName } from "../../Icon/Icon.js";
import { Icon } from "../../Icon/Icon.js";
import { Text } from "../../Text/Text.js";
import { ActorChip } from "../actor.js";
import type { TimelineRow as Row } from "../types.js";
import "./TimelineRow.css";

// The glyph per kind — a redundant channel beside the sentence, aria-hidden.
const kindGlyph: Record<Row["type"], IconName> = {
    ingested_source: "source",
    authored_material: "material",
    edited_material: "pencil",
    packaged_material: "export",
    deleted_resource: "trash",
    restored_resource: "undo",
    version_restored: "clock",
    project_started: "folder",
    session_started: "chat",
    asserted_fact: "database",
    retracted_fact: "database",
};

type TimelineRowProps = {
    readonly row: Row;
    // Reveal the subject — open the material / conversation / package tab.
    readonly onReveal?: (row: Row) => void;
    // Expand an edit-burst into the per-resource file-history lens.
    readonly onExpandResource?: (row: Row) => void;
    // Door a fact moment into the Knowledge-bank lens.
    readonly onOpenKnowledge?: (row: Row) => void;
};

const TimelineRow = ({
    row,
    onReveal,
    onExpandResource,
    onOpenKnowledge,
}: TimelineRowProps): ReactNode => {
    // The subject as a reveal-door. A button (keyboard-reachable, ≥24px via
    // CSS) that opens the subject; falls back to plain text when no handler.
    const subject: ReactNode = onReveal ? (
        <button
            type="button"
            className="bilda-hi-row__subject"
            onClick={() => onReveal(row)}
        >
            {row.subject}
        </button>
    ) : (
        <span className="bilda-hi-row__subject bilda-hi-row__subject--static">
            {row.subject}
        </span>
    );

    return (
        <li className="bilda-hi-row">
            <span className="bilda-hi-row__icon" aria-hidden="true">
                <Icon name={kindGlyph[row.type]} size="sm" />
            </span>
            <div className="bilda-hi-row__body">
                <Text as="p" size="sm" className="bilda-hi-row__sentence">
                    <Sentence
                        row={row}
                        subject={subject}
                        onExpandResource={onExpandResource}
                        onOpenKnowledge={onOpenKnowledge}
                    />
                </Text>
                <div className="bilda-hi-row__meta">
                    <ActorChip actor={row.actor} />
                    <Text as="span" size="xs" tone="faint">
                        {row.when}
                    </Text>
                </div>
            </div>
        </li>
    );
};

// The jargon-free sentence, one arm per kind. The subject is the passed door.
const Sentence = ({
    row,
    subject,
    onExpandResource,
    onOpenKnowledge,
}: {
    row: Row;
    subject: ReactNode;
    onExpandResource?: (row: Row) => void;
    onOpenKnowledge?: (row: Row) => void;
}): ReactNode => {
    switch (row.type) {
        case "ingested_source":
            return <Trans>added the source {subject}</Trans>;
        case "authored_material":
            return <Trans>created {subject}</Trans>;
        case "edited_material":
            return (
                <>
                    <Trans>
                        edited {subject} ({row.edits} changes)
                    </Trans>{" "}
                    {onExpandResource && (
                        <button
                            type="button"
                            className="bilda-hi-row__door"
                            onClick={() => onExpandResource(row)}
                        >
                            <Trans>See versions</Trans>
                            <Icon name="chevron-right" size="xs" />
                        </button>
                    )}
                </>
            );
        case "packaged_material":
            return <Trans>packaged {subject} as HTML</Trans>;
        case "deleted_resource":
            return <Trans>deleted {subject}</Trans>;
        case "restored_resource":
            return <Trans>restored {subject}</Trans>;
        case "version_restored":
            return (
                <Trans>
                    rolled {subject} back to {row.fromVersion}
                </Trans>
            );
        case "project_started":
            return <Trans>started the project {subject}</Trans>;
        case "session_started":
            return <Trans>started a conversation, {subject}</Trans>;
        case "asserted_fact":
            return (
                <>
                    <Trans>noted a fact: {subject}</Trans>{" "}
                    {onOpenKnowledge && (
                        <button
                            type="button"
                            className="bilda-hi-row__door"
                            onClick={() => onOpenKnowledge(row)}
                        >
                            <Trans>Knowledge bank</Trans>
                            <Icon name="external-link" size="xs" />
                        </button>
                    )}
                </>
            );
        case "retracted_fact":
            return <Trans>removed a fact: {subject}</Trans>;
    }
};
